import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { fetchDashboard } from '../api/dashboard';
import { listWorkflowCategories } from '../api/workflow';
import PageHeader from '../components/layout/PageHeader';
import Card from '../components/ui/Card';
import { FullScreenSpinner } from '../components/ui/Spinner';
import StatCard from '../components/dashboard/StatCard';
import MonthlyChart from '../components/dashboard/MonthlyChart';
import PipelineFunnel from '../components/dashboard/PipelineFunnel';
import StuckCandidatesCard from '../components/dashboard/StuckCandidatesCard';
import RecentCandidatesTable from '../components/dashboard/RecentCandidatesTable';
import {
  IconUsers,
  IconClock,
  IconCheckCircle,
  IconXCircle,
  IconReports,
  IconWorkflow,
  IconChevronDown,
  IconLeads,
} from '../components/ui/icons';

const STAT_TILES = [
  { key: 'total', label: 'Total Candidates', icon: IconUsers, tone: 'neutral' },
  { key: 'in_progress', label: 'In Progress', icon: IconClock, tone: 'amber' },
  { key: 'hired', label: 'Hired', icon: IconCheckCircle, tone: 'green' },
  { key: 'rejected', label: 'Rejected', icon: IconXCircle, tone: 'red' },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [categoryId, setCategoryId] = useState('');

  const dashboardQuery = useQuery({
    queryKey: ['dashboard', categoryId],
    queryFn: () => fetchDashboard(categoryId ? { category_id: categoryId } : {}),
  });
  const categoriesQuery = useQuery({ queryKey: ['workflow-categories'], queryFn: listWorkflowCategories });

  if (dashboardQuery.isLoading) return <FullScreenSpinner />;

  if (dashboardQuery.isError) {
    return (
      <div>
        <PageHeader title="Dashboard" />
        <Card>
          <p className="text-sm text-red-600">Could not load dashboard data. Please try again.</p>
        </Card>
      </div>
    );
  }

  const d = dashboardQuery.data;
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const totalRegistered = d.monthly_registrations.reduce((sum, r) => sum + r.count, 0);
  const totalHired = d.monthly_hires.reduce((sum, r) => sum + r.count, 0);

  const pipelineTotal = d.pipeline?.[0]?.count ?? 0;
  const pipelineConversion =
    pipelineTotal > 0 ? Math.round(((d.pipeline[d.pipeline.length - 1]?.count ?? 0) / pipelineTotal) * 100) : null;

  return (
    <div>
      <PageHeader title="Dashboard" />

      <div className="relative mb-6 flex items-center justify-between overflow-hidden rounded-2xl border border-gray-100 bg-gradient-to-br from-primary-50/70 via-white to-white p-6 shadow-soft">
        <div className="relative z-10">
          <h2 className="text-lg font-semibold tracking-tight text-gray-900">
            Welcome back, {user?.name?.split(' ')[0]}.
          </h2>
          <p className="mt-1 text-sm text-gray-500">Here's what's happening with your recruitment pipeline today.</p>
        </div>
        <div className="relative z-10 hidden shrink-0 rounded-full border border-gray-100 bg-white/70 px-4 py-1.5 text-xs font-medium text-gray-500 sm:block">
          {today}
        </div>
        <div aria-hidden className="pointer-events-none absolute -right-8 -top-14 h-44 w-44 rounded-full bg-primary/10 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-16 right-32 h-32 w-32 rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div
        className={`mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 ${d.leads ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}
      >
        {STAT_TILES.map((tile) => (
          <StatCard key={tile.key} label={tile.label} value={d.totals[tile.key]} icon={tile.icon} tone={tile.tone} />
        ))}
        {d.leads && (
          <StatCard label="Leads" value={d.leads.total} icon={IconLeads} tone="blue" />
        )}
      </div>

      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <div className="mb-5 flex items-start justify-between gap-4 border-b border-gray-100 pb-4">
            <div>
              <h3 className="flex items-center gap-2.5 text-[15px] font-semibold tracking-tight text-gray-900">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary">
                  <IconReports className="h-4 w-4" />
                </span>
                Registrations vs. Hires
              </h3>
              <p className="mt-1 pl-[42px] text-xs text-gray-400">Last 12 months</p>
            </div>
            <div className="flex shrink-0 items-start gap-5">
              <div className="text-right">
                <p className="text-lg font-semibold tracking-tight text-gray-900">{totalRegistered.toLocaleString()}</p>
                <p className="text-xs text-gray-400">Registered</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold tracking-tight text-gray-900">{totalHired.toLocaleString()}</p>
                <p className="text-xs text-gray-400">Hired</p>
              </div>
            </div>
          </div>
          <MonthlyChart registrations={d.monthly_registrations} hires={d.monthly_hires} />
        </Card>

        <Card>
          <div className="mb-5 flex items-start justify-between gap-4 border-b border-gray-100 pb-4">
            <div>
              <h3 className="flex items-center gap-2.5 text-[15px] font-semibold tracking-tight text-gray-900">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary">
                  <IconWorkflow className="h-4 w-4" />
                </span>
                Pipeline funnel
              </h3>
              <p className="mt-1 pl-[42px] text-xs text-gray-400">
                {pipelineTotal.toLocaleString()} total
                {pipelineConversion !== null && <> &middot; {pipelineConversion}% conversion</>}
              </p>
            </div>
            <div className="relative shrink-0">
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="appearance-none rounded-full border border-gray-200 bg-white py-1.5 pl-3.5 pr-9 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:border-gray-300 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">All categories</option>
                {(categoriesQuery.data || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <IconChevronDown className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            </div>
          </div>
          <PipelineFunnel steps={d.pipeline} />
        </Card>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <StuckCandidatesCard stuck={d.stuck_candidates} />
        </div>
        <div className="lg:col-span-2">
          <RecentCandidatesTable candidates={d.recent_candidates} />
        </div>
      </div>
    </div>
  );
}
